export type Timeline = "now" | "three_months" | "six_months" | "researching";
export type Ownership = "owner" | "authorized" | "tenant" | "community";
export type LeadTier = "A" | "B" | "C" | "unqualified";

export interface LeadFormData {
  name: string;
  email: string;
  phone: string;
  postalCode: string;
  province: string;
  ownership: Ownership;
  timeline: Timeline;
  monthlyBillEuro: number;
  roofAreaM2: number;
  wantsBattery: boolean;
  notes: string;
  consent: boolean;
}

export interface LeadQualification {
  tier: LeadTier;
  score: number;
  reasons: string[];
  estimatedLeadValueEuro: {
    low: number;
    high: number;
  };
  nextStep: string;
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^(?:\+34\s?)?[6789](?:\s?\d){8}$/;

export function validateLead(data: LeadFormData): string[] {
  const errors: string[] = [];

  if (data.name.trim().length < 2) errors.push("Indica tu nombre.");
  if (!emailRegex.test(data.email.trim())) errors.push("Indica un email válido.");
  if (!phoneRegex.test(data.phone.replace(/[.-]/g, " ").trim())) errors.push("Indica un teléfono español válido.");
  if (!/^\d{5}$/.test(data.postalCode.trim())) errors.push("Indica un código postal español de 5 dígitos.");
  if (data.province.trim().length < 2) errors.push("Indica la provincia.");
  if (data.monthlyBillEuro < 35) errors.push("La factura mensual indicada es demasiado baja para priorizar presupuesto solar.");
  if (data.roofAreaM2 < 10) errors.push("La superficie disponible debe ser al menos 10 m².");
  if (!data.consent) errors.push("Debes aceptar el contacto para solicitar presupuesto.");

  return errors;
}

export function qualifyLead(data: LeadFormData): LeadQualification {
  let score = 0;
  const reasons: string[] = [];

  if (data.ownership === "owner" || data.ownership === "authorized") {
    score += 28;
    reasons.push("Tiene capacidad declarada para decidir o autorizar la instalación.");
  } else if (data.ownership === "community") {
    score += 14;
    reasons.push("Puede ser oportunidad comunitaria, pero necesita coordinación adicional.");
  } else {
    score -= 12;
    reasons.push("Alquiler sin autorización reduce probabilidad de cierre.");
  }

  if (data.timeline === "now") {
    score += 26;
    reasons.push("Quiere presupuesto inmediato.");
  } else if (data.timeline === "three_months") {
    score += 18;
    reasons.push("Plazo de decisión inferior a 3 meses.");
  } else if (data.timeline === "six_months") {
    score += 8;
    reasons.push("Interés medio plazo.");
  } else {
    score += 2;
    reasons.push("Está investigando, requiere nurturing.");
  }

  if (data.monthlyBillEuro >= 140) {
    score += 20;
    reasons.push("Factura alta: mayor potencial económico.");
  } else if (data.monthlyBillEuro >= 85) {
    score += 14;
    reasons.push("Factura suficiente para evaluar autoconsumo.");
  } else {
    score += 4;
    reasons.push("Factura moderada: revisar retorno antes de visita.");
  }

  if (data.roofAreaM2 >= 35) {
    score += 14;
    reasons.push("Superficie aparente compatible con instalación residencial útil.");
  } else if (data.roofAreaM2 >= 20) {
    score += 8;
    reasons.push("Superficie posible, pendiente de validación técnica.");
  } else {
    score += 2;
    reasons.push("Superficie ajustada.");
  }

  if (data.wantsBattery) {
    score += 5;
    reasons.push("Interés en batería aumenta ticket, sujeto a dimensionado horario.");
  }

  if (data.notes.trim().length > 20) {
    score += 5;
    reasons.push("Aporta contexto adicional para cualificar.");
  }

  score = Math.max(0, Math.min(100, score));

  if (score >= 78) {
    return {
      tier: "A",
      score,
      reasons,
      estimatedLeadValueEuro: { low: 70, high: 160 },
      nextStep: "Llamada en menos de 1 hora laboral y asignación a instalador por zona."
    };
  }

  if (score >= 54) {
    return {
      tier: "B",
      score,
      reasons,
      estimatedLeadValueEuro: { low: 35, high: 80 },
      nextStep: "Verificar propiedad, tejado y factura antes de transferir a instalador."
    };
  }

  if (score >= 30) {
    return {
      tier: "C",
      score,
      reasons,
      estimatedLeadValueEuro: { low: 10, high: 35 },
      nextStep: "Enviar guía y recontactar si mejora plazo o capacidad de decisión."
    };
  }

  return {
    tier: "unqualified",
    score,
    reasons,
    estimatedLeadValueEuro: { low: 0, high: 0 },
    nextStep: "No monetizar todavía; aportar contenido educativo y captar email."
  };
}

export function redactLeadForAnalytics(data: LeadFormData) {
  return {
    postalCodePrefix: data.postalCode.slice(0, 2),
    province: data.province,
    ownership: data.ownership,
    timeline: data.timeline,
    monthlyBillBucket:
      data.monthlyBillEuro >= 140 ? "140_plus" : data.monthlyBillEuro >= 85 ? "85_139" : "below_85",
    roofAreaBucket: data.roofAreaM2 >= 35 ? "35_plus" : data.roofAreaM2 >= 20 ? "20_34" : "below_20",
    wantsBattery: data.wantsBattery
  };
}
